/**
 * Detail view of a COHORT (F2-A) with tabs: overview / roster / groups /
 * modules. Teacher+admin view. Enrollment transitions are
 * server-authoritative: activate/promote go through REST (/api/cohorts/...),
 * withdraw and cohort status changes go through the write-path (validated
 * server-side).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Plus } from "@fleet/icons";
import {
  addCohortGroupMemberMutation,
  attachIdentity,
  createCohortGroupMutation,
  DEFAULT_COHORT_MODALITY,
  gradingSchemeManifests,
  handleApiResponse,
  sortByIdentityName,
  transitionCohortEnrollmentMutation,
  updateCohortMutation,
  upsertCohortModuleSettingMutation,
} from "@space/shared";
// AUTONOMY: premium selects + info-popovers for non-obvious fields.
import { Select } from "@/components/ui/select";
import { PillTabPanels, PillTabs } from "@/components/ui/pill-tabs";
import { InfoHint } from "@/components/ui/info-hint";
import { api } from "@/lib/api";
import { Link, useNavigate } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { NotFoundState } from "@/components/ui/not-found-state";
import { showToast } from "@/components/ui/toast";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { ExportButton } from "@/features/io/components/export-button";
import { belongsToActiveOrg, useActiveOrg } from "../hooks/use-active-org";
import { useModalityOptions } from "../hooks/use-modality-options";
import { CohortScheduleTab } from "./cohort-schedule-tab";
import { CohortAttendanceTab } from "./cohort-attendance-tab";
import {
  useCohort,
  useCohortGroupMembers,
  useCohortGroups,
  useCohortModuleSettings,
  useCohortRoster,
} from "../hooks/use-cohort-data";
import { useCohortIdentities } from "../hooks/use-org-identities";
import { useCourseModules } from "../hooks/use-course-modules";
import { GradesTab } from "../assessment/grades-tab";
import { CompletionTab } from "../completion/completion-tab";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { ClassroomChat } from "./classroom/classroom-chat";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

type Tab =
  | "overview"
  | "roster"
  | "groups"
  | "modules"
  | "horario"
  | "asistencia"
  | "grades"
  | "completion"
  // B2: classroom chat moderation (tab with its own flag).
  | "chat";

const TABS: { id: Tab; label: MessageDescriptor }[] = [
  { id: "overview", label: msg`Overview` },
  { id: "roster", label: msg`Roster` },
  { id: "groups", label: msg`Groups` },
  { id: "modules", label: msg`Modules` },
  { id: "horario", label: msg`Schedule` },
  { id: "asistencia", label: msg`Attendance` },
  { id: "grades", label: msg`Grades` },
  { id: "completion", label: msg`Completion` },
];

/** B2: the chat tab only exists if the academy has chat turned on. */
const CHAT_TAB: { id: Tab; label: MessageDescriptor } = { id: "chat", label: msg`Chat` };

export const CohortDetailView: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db } = usePowerSyncContext();
  const { activeOrgId } = useActiveOrg();
  const cohort = useCohort(() => props.cohortId);
  const [tab, setTab] = createSignal<Tab>("overview");
  // B2 · classroom chat is moderated from here, and its tab appears ONLY
  // with the flag turned on. Hiding it is a courtesy: the write-path
  // remains authoritative, discarding hide/pin from whoever doesn't moderate.
  const chatFlag = useFeatureEnabled("academy.classroom.chat");
  const tabItems = createMemo(() =>
    [...TABS, ...(chatFlag().enabled ? [CHAT_TAB] : [])].map((td) => ({
      id: td.id,
      label: t(td.label),
    })),
  );

  // Multi-org isolation: a deep-linked cohort from ANOTHER org is not operable.
  const inActiveOrg = createMemo(
    () =>
      !cohort.data || belongsToActiveOrg(cohort.data.organization_id ?? undefined, activeOrgId()),
  );

  // LF-B: distinguishes loading / doesn't-exist / offline-not-synced-yet — a
  // cohort missing during the initial sync must NOT flash "not found".
  const resolution = createEntityResolution({
    db,
    hasEntity: () => Boolean(cohort.data),
    isPending: () => cohort.isLoading,
  });

  return (
    <div class="mx-auto max-w-3xl space-y-5 p-4 md:p-6">
      <Link to="/academy/cohorts" class="text-sm text-muted-foreground hover:text-foreground">
        ← {t(msg`Cohorts`)}
      </Link>
      <Show
        when={inActiveOrg() ? cohort.data : undefined}
        fallback={
          <Show
            when={resolution() !== "loading" || !inActiveOrg()}
            fallback={<Skeleton class="h-40 w-full rounded-2xl" />}
          >
            <div data-testid="cohort-not-found">
              <NotFoundState
                variant={resolution() === "offline" ? "offline" : "no-access"}
                title={t(msg`Cohort not available`)}
                description={
                  resolution() === "offline"
                    ? t(
                        msg`You're offline and this cohort hasn't synced to this device yet. Reconnect and try again.`,
                      )
                    : t(msg`Cohort not found in this academy.`)
                }
                primaryAction={{
                  label: t(msg`Back to cohorts`),
                  onClick: () => void navigate({ to: "/academy/cohorts" }),
                }}
              />
            </div>
          </Show>
        }
      >
        {(c) => (
          <>
            <div class="flex items-center justify-between gap-3">
              <h1 class="font-display text-2xl font-extrabold" data-testid="cohort-detail-name">
                {c().name}
              </h1>
              <div class="flex items-center gap-2">
                {/* F4-B: multi-sheet cohort workbook (roster+attendance+grades+payments). */}
                <ExportButton
                  workbookKey="io.workbook.cohort"
                  orgId={activeOrgId()}
                  filters={{ cohort_id: props.cohortId }}
                />
                <span class="rounded-full bg-accent/12 px-3 py-1 text-xs font-bold text-accent">
                  {c().status}
                </span>
              </div>
            </div>
            <PillTabs
              tabs={tabItems()}
              value={tab()}
              onChange={setTab}
              testIdPrefix="cohort-tab"
              label={t(msg`Cohort sections`)}
            />
            <PillTabPanels value={tab()} testIdPrefix="cohort-tab">
              <Show when={tab() === "overview"}>
                <OverviewTab cohortId={props.cohortId} />
              </Show>
              <Show when={tab() === "roster"}>
                <RosterTab cohortId={props.cohortId} organizationId={c().organization_id ?? ""} />
              </Show>
              <Show when={tab() === "groups"}>
                <GroupsTab cohortId={props.cohortId} organizationId={c().organization_id ?? ""} />
              </Show>
              <Show when={tab() === "modules"}>
                <ModulesTab
                  cohortId={props.cohortId}
                  courseId={c().course_id ?? undefined}
                  organizationId={c().organization_id ?? ""}
                />
              </Show>
              <Show when={tab() === "horario"}>
                <CohortScheduleTab
                  cohortId={props.cohortId}
                  organizationId={c().organization_id ?? ""}
                  campusId={c().campus_id ?? null}
                />
              </Show>
              <Show when={tab() === "asistencia"}>
                <CohortAttendanceTab
                  cohortId={props.cohortId}
                  organizationId={c().organization_id ?? ""}
                  campusId={c().campus_id ?? null}
                />
              </Show>
              <Show when={tab() === "grades"}>
                <GradesTab
                  cohortId={props.cohortId}
                  courseId={c().course_id ?? undefined}
                  organizationId={c().organization_id ?? ""}
                />
              </Show>
              <Show when={tab() === "chat" && chatFlag().enabled}>
                <ClassroomChat
                  cohortId={props.cohortId}
                  organizationId={c().organization_id}
                  // Moderation IS active here: this screen belongs to the teaching tier.
                  canModerate
                  locked={Boolean(c().chat_locked)}
                />
              </Show>
              <Show when={tab() === "completion"}>
                <CompletionTab
                  cohortId={props.cohortId}
                  organizationId={c().organization_id ?? ""}
                  courseId={c().course_id ?? undefined}
                />
              </Show>
            </PillTabPanels>
          </>
        )}
      </Show>
    </div>
  );
};

const OverviewTab: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const cohort = useCohort(() => props.cohortId);
  // Isolation: the caller's ACTIVE org, not `cohort.data.organization_id`
  // (that would be the data of the row being written — it protects nothing).
  const { activeOrgId } = useActiveOrg();
  // §L1: modalities offerable in THIS cohort's org, preserving its own.
  const modalityOptions = useModalityOptions({
    orgId: () => cohort.data?.organization_id ?? undefined,
    current: () => cohort.data?.modality,
  });

  const save = (patch: Parameters<typeof updateCohortMutation>[2]) => {
    const db = drizzle();
    if (db) {
      void updateCohortMutation(db, props.cohortId, patch, activeOrgId()).catch(() => {
        showToast(t(msg`Could not save the cohort`), "error");
      });
    }
  };

  return (
    <Show when={cohort.data}>
      {(c) => (
        <div class="space-y-3 rounded-2xl border border-border p-5" data-testid="cohort-overview">
          <input
            class={field}
            data-testid="cohort-edit-name"
            value={c().name ?? ""}
            onChange={(e) => save({ name: e.currentTarget.value })}
          />
          <div class="grid grid-cols-2 gap-3">
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`Start date`)}
              <input
                type="date"
                class={field}
                data-testid="cohort-edit-start"
                value={c().start_date ?? ""}
                onChange={(e) => save({ start_date: e.currentTarget.value || null })}
              />
            </label>
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`End date`)}
              <input
                type="date"
                class={field}
                data-testid="cohort-edit-end"
                value={c().end_date ?? ""}
                onChange={(e) => save({ end_date: e.currentTarget.value || null })}
              />
            </label>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`Capacity (0 = unlimited)`)}
              <input
                type="number"
                min="0"
                class={field}
                data-testid="cohort-edit-capacity"
                value={c().capacity ?? 0}
                onChange={(e) => save({ capacity: Number(e.currentTarget.value) || 0 })}
              />
            </label>
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`Status`)}
              <select
                class={field}
                data-testid="cohort-edit-status"
                value={c().status ?? "draft"}
                onChange={(e) => save({ status: e.currentTarget.value })}
              >
                <option value="draft">draft</option>
                <option value="open">open</option>
                <option value="running">running</option>
                <option value="completed">completed</option>
                <option value="cancelled">cancelled</option>
              </select>
            </label>
          </div>

          {/* ── AUTONOMY: modality/rule/scale editable POST-creation ──
              (they used to be sealed when the cohort was created — an operator gap). */}
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div class="space-y-1 text-xs text-muted-foreground">
              <span>
                {t(msg`Modality`)}{" "}
                <InfoHint>
                  {t(
                    msg`In-person and hybrid cohorts use campus rooms and roll-call attendance; live online cohorts use the meeting provider; self-paced has no schedule.`,
                  )}
                </InfoHint>
              </span>
              {/* §L1: in-person options are only offered if the academy uses
                  in-person mode; the one the cohort ALREADY has is preserved. */}
              <Select
                aria-label={t(msg`Modality`)}
                value={c().modality ?? DEFAULT_COHORT_MODALITY}
                onChange={(v) => save({ modality: v })}
                options={modalityOptions().map((o) => ({ value: o.value, label: t(o.label) }))}
              />
            </div>
            <div class="space-y-1 text-xs text-muted-foreground">
              <span>
                {t(msg`Enrollment rule`)}{" "}
                <InfoHint>
                  {t(
                    msg`Open: students enroll directly while there is capacity. Approval: requests wait for an admin. Waitlist: when full, students queue and are auto-promoted (and notified) when a spot frees up.`,
                  )}
                </InfoHint>
              </span>
              <Select
                aria-label={t(msg`Enrollment rule`)}
                value={c().enrollment_rule ?? "open"}
                onChange={(v) => save({ enrollment_rule: v })}
                options={[
                  { value: "open", label: t(msg`Open`) },
                  { value: "approval", label: t(msg`Approval`) },
                  { value: "waitlist", label: t(msg`Waitlist`) },
                ]}
              />
            </div>
            <div class="space-y-1 text-xs text-muted-foreground">
              <span>
                {t(msg`Grading scale`)}{" "}
                <InfoHint>
                  {t(
                    msg`Overrides the course/academy scale for this cohort only. Grades already sealed keep their scale.`,
                  )}
                </InfoHint>
              </span>
              <Select
                aria-label={t(msg`Grading scale`)}
                value={c().grading_scheme ?? ""}
                onChange={(v) => save({ grading_scheme: v || null })}
                options={[
                  { value: "", label: t(msg`Inherit`) },
                  ...gradingSchemeManifests().map((s) => ({
                    value: s.key.replace(/^academy\.grading\./, ""),
                    label: s.name,
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </Show>
  );
};

const RosterTab: Component<{ cohortId: string; organizationId: string }> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  // Isolation: the caller's ACTIVE org (see note in `OverviewTab.save`).
  const { activeOrgId } = useActiveOrg();
  const enrollments = useCohortRoster(() => props.cohortId);
  // D3 (plan 06 · E3): the enrollment still arrives via sync; name and email
  // are served by the single source of identity and stitched together here.
  const people = useCohortIdentities(() => props.cohortId);
  const roster = createMemo(() =>
    sortByIdentityName(
      attachIdentity(enrollments.data ?? [], people.identities(), (r) => r.user_id),
      (r) => r.user_id ?? r.id,
    ),
  );

  const activate = async (userId: string | null) => {
    if (!userId) {
      return;
    }
    try {
      await handleApiResponse(
        api.POST("/api/cohorts/{id}/enrollments/{user_id}/activate", {
          params: { path: { id: props.cohortId, user_id: userId } },
        }),
      );
      showToast(t(msg`Enrollment activated`), "success");
    } catch {
      showToast(t(msg`Could not activate (cohort may be full)`), "error");
    }
  };

  const withdraw = (enrollmentId: string) => {
    const db = drizzle();
    if (db) {
      void transitionCohortEnrollmentMutation(db, enrollmentId, "withdrawn", activeOrgId()).catch(
        () => {
          showToast(t(msg`Could not withdraw the enrollment`), "error");
        },
      );
    }
  };

  const promoteWaitlist = async () => {
    try {
      await handleApiResponse(
        api.POST("/api/cohorts/{id}/promote-waitlist", {
          params: { path: { id: props.cohortId } },
        }),
      );
      showToast(t(msg`Waitlist promoted`), "success");
    } catch {
      showToast(t(msg`Could not promote the waitlist`), "error");
    }
  };

  return (
    <div class="space-y-3" data-testid="cohort-roster">
      <div class="flex justify-end">
        <Button
          variant="outline"
          data-testid="promote-waitlist"
          onClick={() => void promoteWaitlist()}
        >
          {t(msg`Promote waitlist`)}
        </Button>
      </div>
      <Show
        when={roster().length > 0}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`No students yet.`)}</p>}
      >
        <ul class="space-y-2">
          <For each={roster()}>
            {(r) => (
              <li
                class="flex items-center justify-between rounded-xl border border-border p-3"
                data-testid="roster-row"
                data-enrollment-status={r.status ?? ""}
              >
                <div>
                  <p class="text-sm font-semibold">{r.name ?? r.email ?? r.user_id}</p>
                  <p class="text-xs text-muted-foreground">
                    {r.status}
                    <Show when={r.waitlist_position != null}> · #{r.waitlist_position}</Show>
                  </p>
                </div>
                <div class="flex gap-2">
                  <Show when={r.status === "pre_enrolled" || r.status === "waitlisted"}>
                    <button
                      type="button"
                      data-testid="roster-activate"
                      onClick={() => void activate(r.user_id)}
                      class="text-xs font-bold text-primary hover:underline"
                    >
                      {t(msg`Activate`)}
                    </button>
                  </Show>
                  <Show
                    when={
                      r.status === "active" ||
                      r.status === "pre_enrolled" ||
                      r.status === "waitlisted"
                    }
                  >
                    <button
                      type="button"
                      data-testid="roster-withdraw"
                      onClick={() => withdraw(r.id)}
                      class="text-xs text-muted-foreground hover:text-danger"
                    >
                      {t(msg`Withdraw`)}
                    </button>
                  </Show>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
};

const GroupsTab: Component<{ cohortId: string; organizationId: string }> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const groups = useCohortGroups(() => props.cohortId);
  const members = useCohortGroupMembers(() => props.cohortId);
  const enrollments = useCohortRoster(() => props.cohortId);
  const people = useCohortIdentities(() => props.cohortId);
  const roster = createMemo(() =>
    attachIdentity(enrollments.data ?? [], people.identities(), (r) => r.user_id),
  );
  const [name, setName] = createSignal("");

  const membersByGroup = createMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of members.data ?? []) {
      const arr = map.get(m.group_id ?? "") ?? [];
      if (m.user_id) {
        arr.push(m.user_id);
      }
      map.set(m.group_id ?? "", arr);
    }
    return map;
  });

  const nameFor = (userId: string) => roster().find((r) => r.user_id === userId)?.name ?? userId;

  const createGroup = async () => {
    const db = drizzle();
    if (!db) {
      return;
    }
    await createCohortGroupMutation(db, {
      cohortId: props.cohortId,
      organizationId: props.organizationId,
      name: name(),
    });
    setName("");
  };

  const addMember = (groupId: string, userId: string) => {
    const db = drizzle();
    if (db && userId) {
      void addCohortGroupMemberMutation(db, {
        groupId,
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        userId,
      });
    }
  };

  return (
    <div class="space-y-3" data-testid="cohort-groups">
      <div class="flex gap-2">
        <input
          class={field}
          data-testid="group-name"
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          placeholder={t(msg`Group name`)}
        />
        <Button data-testid="group-create" onClick={() => void createGroup()} class="gap-1">
          <Plus class="h-4 w-4" /> {t(msg`Add`)}
        </Button>
      </div>
      <For each={groups.data ?? []}>
        {(g) => (
          <div class="rounded-xl border border-border p-3" data-testid="group-row">
            <p class="font-semibold">{g.name}</p>
            <ul class="mt-1 text-sm text-muted-foreground">
              <For each={membersByGroup().get(g.id) ?? []}>{(uid) => <li>{nameFor(uid)}</li>}</For>
            </ul>
            <select
              class={`${field} mt-2`}
              data-testid="group-add-member"
              onChange={(e) => {
                addMember(g.id, e.currentTarget.value);
                e.currentTarget.value = "";
              }}
            >
              <option value="">{t(msg`Add student…`)}</option>
              <For each={roster().filter((r) => r.status === "active")}>
                {(r) => <option value={r.user_id ?? ""}>{r.name ?? r.user_id}</option>}
              </For>
            </select>
          </div>
        )}
      </For>
    </div>
  );
};

const ModulesTab: Component<{
  cohortId: string;
  courseId: string | undefined;
  organizationId: string;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const modules = useCourseModules(() => props.courseId);
  const settings = useCohortModuleSettings(() => props.cohortId);

  const settingFor = (moduleId: string) =>
    (settings.data ?? []).find((s) => s.module_id === moduleId);

  const saveDue = (moduleId: string, dueAt: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    const vis = settingFor(moduleId)?.visibility;
    void upsertCohortModuleSettingMutation(db, {
      cohortId: props.cohortId,
      moduleId,
      organizationId: props.organizationId,
      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      visibility: vis === "visible" || vis === "hidden" ? vis : null,
    });
  };

  return (
    <div class="space-y-2" data-testid="cohort-modules">
      <Show
        when={(modules.data ?? []).length > 0}
        fallback={
          <p class="text-sm text-muted-foreground">{t(msg`No modules in this course yet.`)}</p>
        }
      >
        <For each={modules.data ?? []}>
          {(m) => (
            <div
              class="flex items-center justify-between gap-3 rounded-xl border border-border p-3"
              data-testid="cohort-module-row"
            >
              <span class="rounded-full bg-accent/12 px-2 py-0.5 text-xs font-bold text-accent">
                {m.kind}
              </span>
              <label class="flex flex-1 items-center justify-end gap-2 text-xs text-muted-foreground">
                {t(msg`Due date`)}
                <input
                  type="date"
                  class="rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
                  data-testid="module-due-at"
                  value={(settingFor(m.id)?.due_at ?? "").slice(0, 10)}
                  onChange={(e) => saveDue(m.id, e.currentTarget.value)}
                />
              </label>
            </div>
          )}
        </For>
      </Show>
    </div>
  );
};
